/**
 * VerificationResults - Step 3 of Quick Create
 *
 * Runs compliance check and displays results with:
 * - Pass/fail percentage
 * - Categorized issues (blockers, warnings, info)
 * - Inline upload buttons for missing documents
 * - Continue to workflow button
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { orderBy } from 'firebase/firestore';
import { dualReadJobDocuments } from '@/lib/firestore-helpers';
import { useRunComplianceCheck } from '@/hooks/useCompliance';
import { mapComplianceFindingsToIssues } from '@/types/workflow';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  ShieldCheck,
  FileText,
  AlertTriangle,
  XCircle,
  CheckCircle,
  RefreshCw,
  ArrowRight,
  Loader2,
  Upload,
  Sparkles,
  AlertCircle,
  FileUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import type { DraftJobData, UploadedDocument } from '@/pages/QuickCreate';
import type { WorkflowIssue, IssueCategory } from '@/types/workflow';

// Get required documents based on permit type
function getRequiredDocumentsForTrade(
  trade: string | undefined
): Array<{ category: string; name: string; required: boolean }> {
  const baseRequirements = [
    { category: 'application_form', name: 'Permit Application Form', required: true },
    { category: 'contractor_license', name: 'Contractor License', required: true },
    { category: 'insurance_coi', name: 'Certificate of Insurance', required: true },
    { category: 'owner_auth', name: 'Owner Authorization', required: false },
  ];

  switch (trade) {
    case 'electrical':
      return [
        ...baseRequirements,
        { category: 'load_calculation', name: 'Load Calculation', required: true },
        { category: 'single_line', name: 'Single Line Diagram', required: true },
        { category: 'riser_diagram', name: 'Riser Diagram', required: false },
      ];
    case 'mechanical':
      return [
        ...baseRequirements,
        { category: 'load_calculation', name: 'Manual J/D/S Calculation', required: true },
        { category: 'equipment_specs', name: 'Equipment Specifications', required: true },
        { category: 'energy_calc', name: 'Energy Calculation', required: false },
      ];
    case 'plumbing':
      return [
        ...baseRequirements,
        { category: 'riser_diagram', name: 'Plumbing Riser', required: true },
        { category: 'site_plan', name: 'Site Plan', required: false },
      ];
    case 'roofing':
      return [
        ...baseRequirements,
        { category: 'product_approval', name: 'Product Approval (NOA)', required: true },
        { category: 'sealed_drawings', name: 'Sealed Drawings', required: false },
      ];
    case 'building':
    default:
      return [
        ...baseRequirements,
        { category: 'building_plans', name: 'Building Plans', required: true },
        { category: 'site_plan', name: 'Site Plan', required: false },
        { category: 'survey', name: 'Property Survey', required: false },
      ];
  }
}

// Category configuration
const categoryConfig: Record<
  IssueCategory,
  { label: string; icon: React.ReactNode; color: string }
> = {
  document: { label: 'Documents', icon: <FileText className="h-4 w-4" />, color: 'blue' },
  field: { label: 'Required Fields', icon: <FileText className="h-4 w-4" />, color: 'purple' },
  jurisdiction: {
    label: 'Jurisdiction',
    icon: <AlertCircle className="h-4 w-4" />,
    color: 'orange',
  },
  license: { label: 'License', icon: <CheckCircle className="h-4 w-4" />, color: 'green' },
  trade: { label: 'Trade Requirements', icon: <FileText className="h-4 w-4" />, color: 'cyan' },
};

interface VerificationResultsProps {
  jobId: string;
  contractorId: string;
  draftJob: DraftJobData;
  documents: UploadedDocument[];
  onAddDocuments: () => void;
}

export function VerificationResults({
  jobId,
  contractorId,
  draftJob,
  documents,
  onAddDocuments,
}: VerificationResultsProps) {
  const navigate = useNavigate();
  const { toast } = useToast();

  // State
  const [hasRunCheck, setHasRunCheck] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [issues, setIssues] = useState<{
    blockers: WorkflowIssue[];
    warnings: WorkflowIssue[];
    info: WorkflowIssue[];
  }>({ blockers: [], warnings: [], info: [] });
  const [uploadedDocuments, setUploadedDocuments] = useState<
    { category: string | null; status: string | null }[]
  >([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Compliance check hook
  const { mutate: runComplianceCheck, isPending: isChecking } = useRunComplianceCheck();

  // Get required documents
  const requiredDocuments = useMemo(
    () => getRequiredDocumentsForTrade(draftJob.permitType),
    [draftJob.permitType]
  );

  // Fetch uploaded documents
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setDocumentsLoading(true);
        const snapshot = await dualReadJobDocuments(contractorId, jobId, [
          orderBy('createdAt', 'desc'),
        ]);

        const docs = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            category: data.category || data.complianceCategory || null,
            status: data.status || null,
          };
        });

        setUploadedDocuments(docs);
      } catch (error) {
        console.error('Error fetching documents:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocuments();
  }, [contractorId, jobId]);

  // Calculate missing documents
  const missingDocuments = useMemo(() => {
    return requiredDocuments
      .filter((req) => req.required)
      .filter((req) => !uploadedDocuments.some((doc) => doc.category === req.category));
  }, [requiredDocuments, uploadedDocuments]);

  // Build missing document issues
  const getMissingDocumentIssues = (): WorkflowIssue[] => {
    return missingDocuments.map((req) => ({
      id: `missing-doc-${req.category}`,
      severity: 'blocker' as const,
      category: 'document' as IssueCategory,
      message: `Missing required document: ${req.name}`,
      details: 'This document must be uploaded before permit submission.',
      fixStepId: 'documents' as const,
      fixAction: 'Upload',
      source: 'document_check',
    }));
  };

  // Run compliance check on mount
  useEffect(() => {
    if (!hasRunCheck && !isChecking && !documentsLoading) {
      handleRunCheck();
    }
  }, [documentsLoading]);

  // Handle running the compliance check
  const handleRunCheck = () => {
    runComplianceCheck(
      {
        jobId,
        contractorId,
        permitType: draftJob.permitType,
      },
      {
        onSuccess: (result) => {
          setHasRunCheck(true);

          // Get missing document issues
          const missingDocIssues = getMissingDocumentIssues();

          // Map compliance findings to workflow issues
          if (result.findings && result.findings.length > 0) {
            const mappedIssues = mapComplianceFindingsToIssues(result.findings);
            setIssues({
              blockers: [...missingDocIssues, ...mappedIssues.blockers],
              warnings: mappedIssues.warnings,
              info: mappedIssues.info,
            });
          } else {
            setIssues({
              blockers: missingDocIssues,
              warnings: [],
              info: [],
            });
          }
        },
        onError: (error) => {
          console.error('Compliance check failed:', error);
          setHasRunCheck(true);

          // Show missing documents and error
          const missingDocIssues = getMissingDocumentIssues();
          setIssues({
            blockers: [
              ...missingDocIssues,
              {
                id: 'compliance-error',
                severity: 'blocker',
                category: 'field',
                message: 'Compliance check failed',
                details: error instanceof Error ? error.message : 'Unable to run compliance check',
                fixStepId: 'compliance',
                fixAction: 'Retry',
                source: 'compliance_check',
              },
            ],
            warnings: [],
            info: [],
          });
        },
      }
    );
  };

  // Calculate compliance percentage
  const allIssues = [...issues.blockers, ...issues.warnings, ...issues.info];
  const issuesByCategory = allIssues.reduce((acc, issue) => {
    if (!acc[issue.category]) {
      acc[issue.category] = [];
    }
    acc[issue.category].push(issue);
    return acc;
  }, {} as Record<IssueCategory, WorkflowIssue[]>);

  const totalChecks = Object.keys(categoryConfig).length;
  const passingChecks = hasRunCheck
    ? Object.keys(categoryConfig).filter(
        (cat) => !issuesByCategory[cat as IssueCategory]?.some((i) => i.severity === 'blocker')
      ).length
    : 0;
  const compliancePercent = hasRunCheck ? Math.round((passingChecks / totalChecks) * 100) : 0;

  // Activate job and continue to workflow
  const handleContinueToWorkflow = async () => {
    setIsActivating(true);

    try {
      // Update job status from 'draft' to 'not_started'
      const jobRef = doc(db, 'contractors', contractorId, 'jobs', jobId);
      await updateDoc(jobRef, {
        status: 'not_started',
        updatedAt: Timestamp.now(),
      });

      // Also update the old path for dual-write compatibility
      const oldJobRef = doc(db, 'jobs', jobId);
      try {
        await updateDoc(oldJobRef, {
          status: 'not_started',
          updatedAt: Timestamp.now(),
        });
      } catch {
        // Old path may not exist
      }

      toast({
        title: 'Job activated',
        description: 'Continuing to workflow...',
      });

      // Navigate to workflow
      navigate(`/jobs/${jobId}`);
    } catch (error: any) {
      console.error('Failed to activate job:', error);
      toast({
        title: 'Error',
        description: 'Failed to activate job. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsActivating(false);
    }
  };

  // Render severity icon
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'blocker':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
    }
  };

  // Render severity badge
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'blocker':
        return (
          <Badge variant="destructive" className="text-xs">
            Blocker
          </Badge>
        );
      case 'warning':
        return (
          <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
            Warning
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-xs">
            Info
          </Badge>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Verification Results
          </CardTitle>
          <CardDescription>
            {draftJob.title} &bull; {draftJob.jurisdictionName || `${draftJob.county} County`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Loading State */}
          {(isChecking || documentsLoading) && !hasRunCheck && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground">Running compliance verification...</p>
            </div>
          )}

          {/* Results */}
          {hasRunCheck && (
            <div className="space-y-4">
              {/* Score Display */}
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    'w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold',
                    compliancePercent === 100
                      ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-200'
                      : compliancePercent >= 50
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200'
                      : 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-200'
                  )}
                >
                  {compliancePercent}%
                </div>
                <div className="flex-1">
                  <Progress
                    value={compliancePercent}
                    className={cn('h-3', compliancePercent === 100 && '[&>div]:bg-green-500')}
                  />
                  <div className="flex justify-between mt-2 text-sm text-muted-foreground">
                    <span className={issues.blockers.length > 0 ? 'text-red-600 font-medium' : ''}>
                      {issues.blockers.length} Blockers
                    </span>
                    <span>{issues.warnings.length} Warnings</span>
                    <span>{issues.info.length} Info</span>
                  </div>
                </div>
              </div>

              {/* Re-run Button */}
              <Button variant="outline" size="sm" onClick={handleRunCheck} disabled={isChecking}>
                {isChecking ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Re-run Check
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Issues by Category */}
      {hasRunCheck && allIssues.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Issues Found</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" defaultValue={['document']} className="space-y-2">
              {(Object.keys(categoryConfig) as IssueCategory[]).map((category) => {
                const categoryIssues = issuesByCategory[category] || [];
                if (categoryIssues.length === 0) return null;

                const hasBlockers = categoryIssues.some((i) => i.severity === 'blocker');
                const config = categoryConfig[category];

                return (
                  <AccordionItem key={category} value={category} className="border rounded-lg">
                    <AccordionTrigger className="px-4 py-2 hover:no-underline">
                      <div className="flex items-center gap-2">
                        {config.icon}
                        <span className="font-medium">{config.label}</span>
                        <Badge
                          variant={hasBlockers ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {categoryIssues.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-3">
                        {categoryIssues.map((issue) => (
                          <div
                            key={issue.id}
                            className={cn(
                              'p-3 rounded-lg border',
                              issue.severity === 'blocker'
                                ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800'
                                : issue.severity === 'warning'
                                ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800'
                                : 'bg-muted/50'
                            )}
                          >
                            <div className="flex items-start gap-2">
                              {getSeverityIcon(issue.severity)}
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium">{issue.message}</p>
                                  {getSeverityBadge(issue.severity)}
                                </div>
                                {issue.details && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {issue.details}
                                  </p>
                                )}
                              </div>
                              {issue.category === 'document' && issue.fixAction === 'Upload' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={onAddDocuments}
                                  className="shrink-0"
                                >
                                  <Upload className="h-3 w-3 mr-1" />
                                  Upload
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {hasRunCheck && issues.blockers.length === 0 && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-green-700 dark:text-green-300">
                  Ready for Submission!
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  All required documents are in place and no blockers found.
                </p>
              </div>
              <Sparkles className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <Card className="border-primary/50 bg-primary/5">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between gap-4">
            <Button variant="outline" onClick={onAddDocuments}>
              <FileUp className="h-4 w-4 mr-2" />
              Add More Documents
            </Button>

            <Button onClick={handleContinueToWorkflow} disabled={isActivating}>
              {isActivating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Activating...
                </>
              ) : (
                <>
                  Continue to Workflow
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center mt-3">
            {issues.blockers.length > 0
              ? 'You can continue with blockers, but they must be resolved before submission.'
              : 'Your permit is ready for the next steps.'}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
